import { useCallback, useEffect, useState } from "react";
import { Box } from "@mui/material";
import { sxStyles } from "./RegisteredAppointmentArea.styles";
import { useNavigate } from "react-router";
import { PATHS } from "lib/routing/paths/paths";
import { RegisteredAppointmentAreaProps } from "./RegisteredAppointmentArea.types";
import ErrorDisplay from "lib/components/error/ErrorDisplay/ErrorDisplay";
import RegisteredAppointmentCard from "./RegisteredAppointmentCard/RegisteredAppointmentCard";
import NonVCQueueDetail from "../../common/registration-queue/NonVCQueueDetail/NonVCQueueDetail";
import VCPQueueDetail from "../../common/registration-queue/VCPQueueDetail/VCPQueueDetail";
import QueueCardSkeleton from "./QueueCardSkeleton";
import {
  formatDate,
  formatOptions,
} from "lib/util/DateTimeUtil/formatDate/formatDate";
import { formatDateToISO } from "lib/util/DateTimeUtil/formatDateToISO/formatDateToISO";
import { useAppDispatch, useAppSelector } from "lib/redux/hooks";
import { selectMemberIdentifier } from "lib/redux/user/selectors";
import {
  setQueueInitializeHasErrored,
  updateDetailsForActualiseAppointment,
  updateAppointmentMetaAppointmentId,
} from "ui/appointment/ducks/appointmentsSlice";
import { fetchQueueStatusAndJourney } from "ui/appointment/ducks/thunks";

const RegisteredAppointmentArea = ({
  appointment,
  queueStatusFlag,
  onRefreshAll,
}: RegisteredAppointmentAreaProps) => {
  const classes = sxStyles();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // Redux States
  const apptDetail = useAppSelector(
    (state) => state.appointments.register[appointment.appointmentId],
  );

  // Don't destructure from apptDetail immediately - this will allow React to properly track changes
  // to the Redux state and re-render when apptDetail changes
  const memberIdentifier = useAppSelector(selectMemberIdentifier);

  // Local States
  const [firstTimeLoading, setFirstTimeLoading] = useState(true);
  const [refreshAt, setRefreshAt] = useState(
    formatDate(formatDateToISO(new Date()), formatOptions.timeWithPeriod),
  );

  // Dispatches
  // Callback invoked when user clicked on the area
  const onClick = (
    appointmentId: string,
    regAppointmentId: string,
    regInstitutionCode: string,
    institutionCode: string,
    sourceSystem: string,
    clinicCode: string | null,
    actualizeStatus: string | null,
    queueNumber: string | null,
    institutionName: string | null,
    consultType: string | null,
    encounterId: string | null,
    appointmentTimeToArrive: string | null,
    vcSupportStatus: string | null,
  ) => {
    dispatch(
      updateDetailsForActualiseAppointment({
        appointmentId,
        regAppointmentId,
        regInstitutionCode,
        institutionCode,
        sourceSystem,
        clinicCode,
        actualizeStatus,
        queueNumber,
        institutionName,
        consultType,
        encounterId,
        appointmentTimeToArrive,
        vcSupportStatus,
      }),
    );
  };

  const onRefresh = ({
    appointmentId,
    regAppointmentId,
    encounterId,
    appointmentTime,
    vcSupportStatus,
  }: {
    appointmentId: string;
    encounterId: string | null;
    regAppointmentId: string | null;
    appointmentTime: string | null;
    vcSupportStatus: string | null;
  }) => {
    dispatch(
      fetchQueueStatusAndJourney({
        appointmentId,
        regAppointmentId,
        encounterId,
        appointmentTime,
        vcSupportStatus,
      }),
    );
  };

  // Callback invoked after refresh of queue status is successfully called
  const resetErrorState = useCallback(
    (appointmentId: string) => {
      dispatch(
        setQueueInitializeHasErrored({ appointmentId, hasErrored: null }),
      );
    },
    [dispatch],
  );

  // Make sure the appointment ID is set in Redux before fetching queue data
  useEffect(() => {
    // First set the appointment ID in Redux so selectApptDetail will work correctly
    dispatch(updateAppointmentMetaAppointmentId(appointment.appointmentId));

    onRefresh({
      appointmentId: appointment.appointmentId || "",
      encounterId: apptDetail?.encounterId || appointment.encounterId,
      appointmentTime: appointment.appointmentStartDateTime || "",
      vcSupportStatus: appointment.vcSupportStatus || "",
      regAppointmentId: appointment.regAppointmentId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    queueStatusFlag,
    apptDetail?.encounterId,
    appointment.appointmentId,
    dispatch,
  ]);

  // update refreshing time only after completion of fetching queue status
  useEffect(() => {
    if (apptDetail?.isLoading === false && apptDetail?.hasErrored === false) {
      setRefreshAt(
        formatDate(formatDateToISO(new Date()), formatOptions.timeWithPeriod),
      );
      setFirstTimeLoading(false);
      resetErrorState(appointment.appointmentId);
    }
  }, [
    apptDetail?.isLoading,
    apptDetail?.hasErrored,
    resetErrorState,
    appointment,
    apptDetail,
  ]);

  return (
    <>
      {apptDetail?.isLoading && firstTimeLoading ? (
        <QueueCardSkeleton />
      ) : apptDetail?.hasErrored ? (
        <Box sx={classes.errorContainer}>
          <ErrorDisplay
            errorMessage={apptDetail?.errorMessage}
            showErrorImage={false}
            showTitle={true}
          />
        </Box>
      ) : (
        <Box sx={classes.QueueDetailBox}>
          <Box
            sx={classes.borderBox}
            data-testid="registered-appt-card"
            onClick={() => {
              if (
                !appointment.isVirtualConsultAppointment &&
                appointment.regInstitutionCode &&
                appointment.institutionCode &&
                appointment.sourceSystem
              ) {
                onClick(
                  appointment.appointmentId,
                  appointment.regAppointmentId,
                  appointment.regInstitutionCode,
                  appointment.institutionCode,
                  appointment.sourceSystem,
                  appointment.clinicCode,
                  appointment.actualizeStatus,
                  appointment.mobileRegQNo,
                  appointment.institutionName,
                  appointment.consultType,
                  apptDetail?.encounterId || appointment.encounterId,
                  appointment.appointmentStartDateTime || "",
                  appointment.vcSupportStatus || "",
                );
                navigate(PATHS.APPOINTMENT_QUEUECHIT_JOURNEY.path);
              }
            }}
          >
            {appointment.isVirtualConsultAppointment ? (
              <VCPQueueDetail
                appointment={appointment}
                onRefreshAll={onRefreshAll}
                institutionName={appointment.institutionName}
                institutionCode={appointment.institutionCode}
                regInstitutionCode={appointment.regInstitutionCode}
                appointmentId={appointment.appointmentId}
                memberIdentifier={memberIdentifier}
                targetSystem={appointment.sourceSystem}
                currentAppointmentTime={appointment.datetime}
                refreshAt={refreshAt}
                vcErrorMsg={appointment.zoomLinkErrorMessage || ""}
                participantURL={appointment.participantURL || ""}
                encounterStatusCode={appointment.encounterStatusCode || null}
                encounterStatus={appointment.encounterStatusDisplay || ""}
                isLoggingProceedToVideoConsultRequired={
                  appointment.isLoggingProceedToVideoConsultRequired || false
                }
                isShowShareVCLinks={appointment.showShareVCLinks || false}
                regAppointmentId={appointment.regAppointmentId}
                queueNumber={appointment.mobileRegQNo || ""}
                meetingId={appointment.formattedZoomMeetingId || null}
                passcode={appointment.zoomMeetingPassword || null}
                clinicCode={appointment.clinicCode || null}
              />
            ) : (
              <NonVCQueueDetail
                institutionName={appointment.institutionName}
                currentStation={apptDetail?.currentStation}
                queueNumber={apptDetail?.queueNumber}
                queueStatus={apptDetail?.queueStatus}
                patientWaiting={apptDetail?.patientWaiting}
                refreshAt={refreshAt}
                statusMessage={apptDetail?.statusMessage}
                showStatus={apptDetail?.showStatusText}
                showPatientWaiting={apptDetail?.showPatientWaiting}
                showRightArrow={true}
                requireCheckIn={apptDetail?.requireCheckIn}
                iHaveArrivedMessage={apptDetail?.ihaveArrivedMessage}
                regAppointmentId={appointment.regAppointmentId}
                appointmentId={appointment.appointmentId}
                institutionCode={appointment.institutionCode}
                regInstitutionCode={appointment.regInstitutionCode}
                sourceSystem={appointment.sourceSystem}
                clinicCode={appointment.clinicCode}
                consultType={appointment.consultType}
                currentClinicCode={apptDetail?.currentClinicCode}
                lateThresholdMessage={apptDetail?.lateThresholdMessage}
                encounterId={appointment.encounterId}
                appointmentTime={appointment.appointmentStartDateTime}
                isFromMobile={false}
                isDiffFromRegApptId={apptDetail?.isDiffFromRegApptId}
                clinicLocation={apptDetail?.clinicLocation}
                disclaimerErrorText={apptDetail?.disclaimerErrorText}
              />
            )}
          </Box>
          {!appointment.isVirtualConsultAppointment ? (
            <Box>
              <RegisteredAppointmentCard appointment={appointment} />
              {appointment.linkedAppointments?.map((appt) => {
                return (
                  <Box key={appt.appointmentId}>
                    <RegisteredAppointmentCard appointment={appt} />
                  </Box>
                );
              })}
            </Box>
          ) : null}
        </Box>
      )}
    </>
  );
};

export default RegisteredAppointmentArea;
